import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function LiveEvents() {
  return (
    <>
      <Meta
        title="Working with Events"
        description="Use the Vimeo Live API to stream live video, return videos from an event, and more."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/live'}>Vimeo Live</Path.Link>
            <Path.Current>Working with Vimeo Events</Path.Current>
          </Path>
        }
        nav={{ page: 'live', subpage: 'events' }}
      >
        <Header>Working with Vimeo Events</Header>

        <Paragraph size="2">
          This brief guide shows you how to work with Vimeo's live streaming capability using our
          API. We cover how to stream an event, how to access event recordings, and how to share
          event archives.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Paragraph size="2">
          Please note that Vimeo's live API is available only to Vimeo Enterprise customers.{' '}
          <Link variant="minimal" href="https://vimeo.com/enterprise?mkc=liveapi">
            Learn more about Vimeo Enterprise.
          </Link>
        </Paragraph>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>Access to the Vimeo Event API.</strong> You must request this; see below for
            more information. To qualify, you must be a{' '}
            <Link variant="minimal" href="https://vimeo.com/enterprise?mkc=liveapi">
              Vimeo Enterprise
            </Link>{' '}
            customer or a Vimeo development partner.
          </li>
          <li>
            <strong>An API application.</strong> This is the app that will handle the stream. If you
            need to register a new app for this purpose, visit our{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>{' '}
            guide for step-by-step instructions.
          </li>
          <li>
            <strong>
              An access token with the{' '}
              <code>
                <b>public</b>
              </code>
              ,{' '}
              <code>
                <b>private</b>
              </code>
              ,{' '}
              <code>
                <b>create</b>
              </code>
              ,{' '}
              <code>
                <b>upload</b>
              </code>
              , and{' '}
              <code>
                <b>edit</b>
              </code>{' '}
              scopes.
            </strong>{' '}
            You can specify others if your app requires them, but you need these five at minimum. To
            learn more about access tokens and scopes, refer to{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>
            .
          </li>
          <li>
            <strong>An application on your computer that can stream live video.</strong> This points
            your live feed to Vimeo through an RTMP URL and stream key.
          </li>
        </ul>

        <Header size="2" id="requesting-live-api-access">
          Requesting access to the Vimeo Events API
        </Header>
        <Paragraph size="2">
          Unlike most of the rest of the Vimeo API, the Vimeo Event's API is available on a
          by-request basis only. If you haven't received live access yet, you'll need to do that
          now.
        </Paragraph>

        <Paragraph size="2">
          First, obtain the client identifier of the API app that you'll be using to stream live
          video. The client identifier appears on the app's information page, which you can access
          from{' '}
          <Link variant="minimal" href="/apps">
            My Apps
          </Link>
          . Click the name of the app to see its technical details.
        </Paragraph>

        <Paragraph size="2">
          Next, request access to the Vimeo Events API from your sales rep or by emailing partner
          support. Have your client identifier ready for your sales rep or include it in your email.
          If you don't have the email address for partner support, reach out to your sales rep and
          they can give it to you.
        </Paragraph>

        <Header size="2" id="streaming-an-event">
          Streaming the event
        </Header>
        <Paragraph size="2">This is the general procedure for streaming an event:</Paragraph>

        <FancyListTableOfContents
          anchorPrefix="streaming-an-event"
          items={[
            'Create the event.',
            'Get the RTMPS link and stream key.',
            'Stream the event.',
            'End the event.',
          ]}
        />

        <Paragraph size="2">Specific details appear in the sections that follow.</Paragraph>

        <FancyList anchorPrefix="streaming-an-event">
          <FancyListItem>
            <Header size="3">Create the event</Header>
            <Paragraph size="2">
              To create the event, send an authenticated POST request to{' '}
              <code>/me/live_events</code>, appending it with{' '}
              <code>
                <b>?fields=</b>
              </code>{' '}
              followed by the specified comma-separated field names to return values for the{' '}
              <code>
                <b>link</b>
              </code>
              ,{' '}
              <code>
                <b>uri</b>
              </code>
              ,{' '}
              <code>
                <b>streamable_clip</b>
              </code>
              , and{' '}
              <code>
                <b>stream_privacy.view</b>
              </code>{' '}
              fields, like this:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'post',
                resource: '/me/live_events?fields=link,uri,streamable_clip,stream_privacy.view',
              }}
            />

            <Paragraph size="2">
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              explains what each of these fields represents.
            </Paragraph>

            <GuideTable
              tableNumber={1}
              description={
                <span>
                  Field descriptions for POST to <code>/me/live_events</code>
                </span>
              }
              headers={['Field name', 'Description']}
              rows={[
                [
                  <code key={'fieldname1'}>
                    <b>link</b>
                  </code>,
                  'The URI used to access the event on Vimeo. Anyone with this information and the right viewing permissions can access the live feed.',
                ],
                [
                  <code key={'fieldname2'}>
                    <b>uri</b>
                  </code>,
                  'The API URI that resolves to the connection data. You use this identifier to interact with the video through the API.',
                ],
                [
                  <code key={'fieldname3'}>
                    <b>streamable_clip</b>
                  </code>,
                  'The video ID of the newly created live session within the event.',
                ],
                [
                  <code key={'fieldname4'}>
                    <b>stream_privacy.view</b>
                  </code>,
                  'The privacy type of the event.',
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> The{' '}
              <code>
                <b>fields</b>
              </code>{' '}
              parameter enables you to return specific fields of a representation, which improves
              response time and helps you to avoid rate limiting. For more information, check out{' '}
              <Link variant="minimal" href="/api/common-formats#json-filter">
                Using Common Formats and Parameters
              </Link>
              . For more information, check out{' '}
              <Link variant="minimal" href="/api/common-formats#json-filter">
                Using Common Formats and Parameters
              </Link>
              . For more details on rate limiting, see{' '}
              <Link variant="minimal" href="/guidelines/rate-limiting">
                Working with Rate Limits
              </Link>
              .
            </StyledNotice>

            <Paragraph size="2">
              Be sure to include the{' '}
              <code>
                <b>Authorization</b>
              </code>
              ,
              <code>
                <b>Content-Type</b>
              </code>
              , and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              headers with your request. See{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>{' '}
              for the values to use.
            </Paragraph>
            <GuideTable
              tableNumber={2}
              description={
                <span>
                  Headers for POST to <code>/me/live_events</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code key="header-1">
                    <b>Authorization</b>
                  </code>,
                  <code key="1">{`bearer {access_token}`}</code>,
                ],
                [
                  <code key="header-2">
                    <b>Content-Type</b>
                  </code>,
                  <code key="2">application/json</code>,
                ],
                [
                  <code key="header-3">
                    <b>Accept</b>
                  </code>,
                  <code key="3">application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />
            <StyledNotice>
              <strong>NOTE:</strong> Don't omit the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header in this request (and all subsequent requests in this guide), and always be sure
              to set it to the given value. This ensures that the API handles your response
              according to version 3.4. For more information, check out{' '}
              <Link variant="minimal" href="/api/common-formats#json-filter">
                Using Common Formats and Parameters
              </Link>
              .
            </StyledNotice>

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>title</b>
              </code>{' '}
              and{' '}
              <code>
                <b>stream_title</b>
              </code>{' '}
              fields. The value of the{' '}
              <code>
                <b>title</b>
              </code>{' '}
              field represents the title of the event, but you can also use it as the base title for
              videos that are created by streaming to the event. The value of the{' '}
              <code>
                <b>stream_title</b>
              </code>{' '}
              field represents the title of the video that's currently being streamed to the event:
            </Paragraph>

            <CodeBlock language="json">
              {{
                title: 'Ponies on Parade',
                stream_title: 'Episode 5: The Majestic Purple Ponies',
              }}
            </CodeBlock>

            <Paragraph size="2">
              If all goes well, you get a video response that looks something like this:
            </Paragraph>

            <CodeBlock language="json">
              {{
                uri: '/live_events/{event_id}',
                link: '/events/{event_id}',
                streamable_clip: '{streamable_clip}',
                stream_privacy: {
                  view: '{stream_privacy.view}',
                },
              }}
            </CodeBlock>
            <Paragraph size="2">
              Remember that you’re using field filtering for this request, so the response fields
              here are limited to the ones that you specifically asked to return. For the complete
              list of fields in the response, see our{' '}
              <Link variant="minimal" href="/api/reference/response/recurring-event">
                reference documentation
              </Link>
              .
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Get the RTMPS link and stream key</Header>
            <Paragraph size="2">
              Event? Created. All you need now for streaming are the upstream RTMPS link and its
              associated stream key. Return these from the API with the following GET request, where
              the{' '}
              <code>
                <b>{`{uri}`}</b>
              </code>{' '}
              placeholder is the value of the{' '}
              <code>
                <b>uri</b>
              </code>{' '}
              field from{' '}
              <Link variant="minimal" href="#table-1">
                Step 1
              </Link>
              :
            </Paragraph>

            <CodeBlock
              endpoint={{ method: 'get', resource: '/me/{uri}?fields=rtmps_link,stream_key' }}
            />
            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              for the headers.
            </Paragraph>
            <GuideTable
              tableNumber={3}
              description={
                <span>
                  Headers for GET to <code>{'/me/{uri}'}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code key="header-1">
                    <b>Authorization</b>
                  </code>,
                  <code key="value1">{`bearer {access_token}`}</code>,
                ],
                [
                  <code key="header2">
                    <b>Accept</b>
                  </code>,
                  <code key="value2">application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              The API sends back the{' '}
              <code>
                <b>rtmps_link</b>
              </code>{' '}
              and
              <code>
                {' '}
                <b>stream_key</b>
              </code>{' '}
              fields, like this:
            </Paragraph>

            <CodeBlock language="json">
              {{
                rtmps_link: 'rtmps://rtmp-global.cloud.vimeo.com:443/live',
                stream_key: '{stream_key}',
              }}
            </CodeBlock>

            <Paragraph size="2">
              You'll need the values of these fields for the next step, so don't try anything funny
              with them.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Stream the event</Header>
            <Paragraph size="2">Time to go live!</Paragraph>
            <Paragraph size="2">
              Remember that practice and preparation lead to a more organized and stress-free event
              experience, both for you as an organizer and also for your viewers.
            </Paragraph>
            <Paragraph size="2">
              Use the RTMPS URL and stream key that you acquired in{' '}
              <Link variant="minimal" href="#streaming-an-event-step-2">
                Step 2
              </Link>{' '}
              to populate your video encoder. This is so we can get your stream going as soon as you
              start sending in content.
            </Paragraph>
            <Paragraph size="2">
              If you want to share out the event embed or copy a link to all your viewers, use the{' '}
              <code>
                <b>link</b>
              </code>{' '}
              field from{' '}
              <Link variant="minimal" href="#streaming-an-event-step-1">
                Step 1
              </Link>
              .
            </Paragraph>

            <CodeBlock endpoint={{ method: 'get', resource: '/videos/{video_id}' }} />
          </FancyListItem>
          <FancyListItem>
            <Header size="3">End the event</Header>
            <Paragraph size="2">
              To end the event, we recommend using the controls that come with your video encoder
              software or even the browser-based production tools.
            </Paragraph>
            <Paragraph size="2">
              When you're streaming from the web, click the{' '}
              <code>
                <b>End event</b>
              </code>{' '}
              button. The live session closes immediately.
            </Paragraph>
            <Paragraph size="2">
              If you're streaming from an external encoder via RTMP, there's an expected delay when
              the encoder has been disconnected that leaves this session open for another five
              minutes. This enables the encoder to reconnect if necessary and continue streaming to
              the same streamable video. However, for the same reason, you can't start a new stream
              to the same event until the five minutes have passed. If you need to end the event
              right away, you can use the events web interface and end the event by clicking the{' '}
              <code>
                <b>End event</b>
              </code>{' '}
              button.
            </Paragraph>
          </FancyListItem>
        </FancyList>
        <Header size="2" id="whats-next">
          Accessing event recordings
        </Header>
        <Paragraph size="2">
          An event contains all the videos that have been streamed to it, which means that you have
          a copy of all your previous live streams.
        </Paragraph>
        <Paragraph size="2">
          To return all the videos associated with an event, send the following GET request, where{' '}
          <code>
            <b>{'{event_id}'}</b>
          </code>{' '}
          represents the ID of the event:
        </Paragraph>
        <CodeBlock endpoint={{ method: 'get', resource: '/me/live_events/{event_id}/videos' }} />
        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-3">
            Table 3
          </Link>{' '}
          for the headers. Send{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          as <code>bearer {'{access_token}'}</code> and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          as <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>
        <Paragraph size="2">
          The HTTP status comes back as 200 OK to let you know that the videos were returned.
        </Paragraph>
        <Header size="2" id="whats-next">
          Sharing the event archives
        </Header>
        <Paragraph size="2">
          While your event is live streaming, its URL is{' '}
          <code>https://vimeo.com/event/{'event_id'}</code>, where{' '}
          <code>
            <b>{'event_id'}</b>
          </code>{' '}
          represents the ID of the event. This ID includes all the videos associated with the event.
          But once you've ended the event, each video within it is archived as its own entity and
          given a unique URL in the format of <code>https://vimeo.com/{'video_id'}</code>. This
          distinguishes the individual videos within an event from the event series after it's no
          longer live. Keep this in mind when sharing links to your event versus a specific archive
          from that event.
        </Paragraph>
        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          We've gone through the steps for streaming an dso on, but the API enables you to do even
          more. See our{' '}
          <Link variant="minimal" href="/api/reference/live">
            API Reference
          </Link>{' '}
          for a complete list of endpoints.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
